/** How often the connection is looked at. */
export const CHECK_EVERY_MS = 1_000;
/** Quiet this long: ask the room (a ping, which it answers without waking). */
export const PING_AFTER_MS = 2_500;
/** Quiet this long, pings and all: the connection is gone, even though it never closed. */
export const DEAD_AFTER_MS = 8_000;
/** After a wake (the page back in front, the network back): an answer within this, or it's gone. */
export const WAKE_ANSWER_MS = 4_000;

export type Liveness = "ok" | "ping" | "dead";

/**
 * Whether a room socket is still alive (P2-WEB-06). A connection can die without closing: a switch
 * from Wi-Fi to cellular, a NAT timeout, a phone that slept with the page open. Anything from the
 * room counts as a sign of life, so during a match (20 frames a second) nothing extra is sent; in a
 * quiet room the client pings, and a socket that stays silent is given up on and the seat resumed.
 * Pure: the caller passes the time and does the pinging.
 */
export class LivenessCheck {
  #heard: number;
  #pinged: number;
  #checked: number;
  /** A wake's check in progress: since when an answer is awaited. */
  #waking: number | null = null;

  constructor(now: number) {
    this.#heard = now;
    this.#pinged = now;
    this.#checked = now;
  }

  /** Something arrived from the room. */
  heard(now: number): void {
    this.#heard = now;
    this.#waking = null;
  }

  /**
   * The page is back in front, or the network is back: the socket may have died meanwhile, so ask
   * now and expect an answer soon. Quiet time before the wake doesn't count against it.
   */
  wake(now: number): "ping" {
    this.#heard = Math.max(this.#heard, now);
    this.#pinged = now;
    this.#checked = now;
    this.#waking = now;
    return "ping";
  }

  /** Every CHECK_EVERY_MS: what to do now. */
  check(now: number): Liveness {
    // Timers stood still (a sleeping laptop, a locked phone): that's a wake, not silence.
    if (now - this.#checked > 3 * CHECK_EVERY_MS) return this.wake(now);
    this.#checked = now;
    if (this.#waking !== null && now - this.#waking >= WAKE_ANSWER_MS) return "dead";
    if (now - this.#heard >= DEAD_AFTER_MS) return "dead";
    if (now - this.#heard >= PING_AFTER_MS && now - this.#pinged >= PING_AFTER_MS) {
      this.#pinged = now;
      return "ping";
    }
    return "ok";
  }
}
